import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Search } from 'lucide-react';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useAuthStore } from '@/auth/auth-store';
import { useFormsList, type QuestionnaireDatum } from '@/services/scheduling/use-forms-list';
import type { AppointmentFormValues } from '@/types/appointment-form';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

interface FormsPickerViewProps {
  /** Closes the view without persisting changes. */
  onCancel: () => void;
}

/**
 * FormsPickerView — full panel-content replacement.
 * Mirrors Flutter `forms_selection_widget.dart`:
 *   - Top bar: Back / Forms / (no Save — selection auto-commits)
 *   - Search input scoped to provider + appointmentType
 *   - List of Active forms with title + question count + Select button
 *   - Empty state copy when the selected provider has no forms
 *
 * On select writes `{ questionnaireId, questionnaireName, questionnaireStatus }`
 * to the form and closes (Flutter forms_selection_widget.dart:171-181).
 */
export function FormsPickerView({ onCancel }: FormsPickerViewProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const providerId = watch('providerId');
  const appointmentType = watch('appointmentType');

  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';

  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 400);

  const { data, isLoading } = useFormsList(
    providerId
      ? {
          organizationId: orgId,
          providerId,
          appointmentType: appointmentType?.code ?? null,
          search: debouncedSearch || undefined,
        }
      : null,
  );

  const list = data?.questionnaireData ?? [];

  const selectForm = (form: QuestionnaireDatum) => {
    if (!form.id) return;
    setValue('questionnaireId', form.id, { shouldValidate: true });
    setValue('questionnaireName', form.title ?? null);
    setValue('questionnaireStatus', form.status ?? null);
    onCancel();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="booking-subhead" data-slot="booking-subhead">
        <button
          onClick={onCancel}
          className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
        >
          Back
        </button>
        <h2 className="booking-subhead-title">Send form</h2>
        <span className="w-10" aria-hidden />
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-4 py-4 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-3 size-4 text-text-hint pointer-events-none" />
            <input
              placeholder="Search forms"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full h-10 pl-10 pr-3 rounded-lg border border-border-default bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:border-brand-primary focus:bg-surface-card focus:outline-none"
            />
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-14 rounded-lg bg-grey-200 animate-pulse" />
              ))}
            </div>
          ) : list.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-8 leading-snug">
              No questionnaire created for the selected provider, please create one to attach here.
            </p>
          ) : (
            <div className="space-y-2">
              {list.map((form, i) => {
                const total = form.totalQuestions ?? 0;
                return (
                  <div
                    key={form.id || `form-${i}`}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border-default bg-surface-card"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-text-primary truncate">
                        {form.title || '—'}
                      </p>
                      <p className="text-xs text-text-muted">
                        {total} {total === 1 ? 'Question' : 'Questions'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => selectForm(form)}
                      className="h-8 px-3 rounded-md bg-grey-100 hover:bg-blue-100 text-sm font-semibold text-brand-primary"
                    >
                      Select
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
